import axios, { AxiosError } from "axios";
import createApiHeadersInterceptor from "../infra/axios/createApiHeadersInterceptor";
import { API_URL } from "../infra/constants/envVariables";
const MOCK_REQUEST_NOT_FOUND_ERROR = "mockRequestNotFoundError";
let baseUrl = "";
let devBaseUrl = "";
let mockBaseUrl = "";
// shared axios client used by business-logic consumers
const axiosClient = axios.create();
// Dedicated client for direct-to-storage uploads (e.g. S3 presigned URLs).
// It intentionally has NO request interceptors: S3 rejects PUTs that include an
// `Authorization` header alongside the presigned query-string credentials,
// returning HTTP 400. Keeping this separate from `axiosClient` prevents the app
// auth/header interceptor from leaking into upload requests.
const uploadClient = axios.create();
const getRetryBaseUrlCandidates = () => {
    return [mockBaseUrl, baseUrl].filter(Boolean);
};
const getWebApiUrl = () => {
    return globalThis.process?.env?.VITE_API_URL ?? API_URL;
};
const replaceRetryBaseUrl = (config, devBase) => {
    const retryBaseUrl = getRetryBaseUrlCandidates().find((candidateBaseUrl) => config.url?.includes(candidateBaseUrl));
    if (retryBaseUrl && config.url) {
        config.url = config.url.replace(retryBaseUrl, devBase);
    }
};
// Interceptor: if mock server returns mockRequestNotFoundError, retry against dev base URL
axiosClient.interceptors.response.use((resp) => resp, async (error) => {
    try {
        const retryError = error;
        const respData = retryError.response?.data || {};
        const config = retryError.config;
        if (respData?.error?.name === MOCK_REQUEST_NOT_FOUND_ERROR && config && !config.isRetry) {
            config.isRetry = true;
            const devBase = devBaseUrl || getWebApiUrl() || baseUrl;
            replaceRetryBaseUrl(config, devBase);
            return axiosClient.request(config);
        }
    }
    catch (e) {
        void e;
    }
    return Promise.reject(error);
});
axiosClient.interceptors.request.use(createApiHeadersInterceptor());
function setBaseUrl(url) {
    baseUrl = url;
    devBaseUrl = url;
}
/**
 * Switch between mock and dev base URLs.
 * If `useMocks` is true the function will prefer the provided `mockUrl`,
 * then current `baseUrl`.
 * When `useMocks` is false it will prefer `devUrl`,
 * then the current web API env value, then current `baseUrl`.
 * Provided URLs are also stored in module-level vars (`mockBaseUrl`, `devBaseUrl`)
 * so the axios retry interceptor can use them as a fallback in environments
 * where `process.env` is unavailable (e.g. React Native).
 */
function setUseMocks(useMocks, mockUrl, devUrl) {
    if (mockUrl)
        mockBaseUrl = mockUrl;
    if (devUrl)
        devBaseUrl = devUrl;
    if (useMocks) {
        const url = mockUrl ?? baseUrl;
        baseUrl = url;
    }
    else {
        const url = devUrl ?? getWebApiUrl() ?? baseUrl;
        baseUrl = url;
    }
}
function getBaseUrlVersioned(version) {
    if (!version || version === "v1") {
        return baseUrl;
    }
    else {
        return `${baseUrl}/${version}`;
    }
}
function formatError(error) {
    if (error && error instanceof AxiosError) {
        return `${error.name} with '[${error.code}/${error.status}]-${error.message} on ${error?.config?.url}`;
    }
    else {
        return `${JSON.stringify(error)}`;
    }
}
export { axiosClient, formatError, getBaseUrlVersioned, setBaseUrl, setUseMocks, uploadClient };
